import { Add, DeleteOutline } from '@mui/icons-material';
import { Box, Divider, IconButton } from '@mui/material';
import type { CSSProperties } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  DIGEST_DEFAULT_MAX_ITEMS,
  DIGEST_DEFAULT_MAX_PAGES,
} from '../../../helpers/knowledgeHelper';
import Button from '../Button/Button';
import Text from '../Text/Text';
import ToolTip from '../ToolTip/ToolTip';
import DigestEntryFields from './DigestEntryFields';

const EMPTY_DIGEST = {
  heading: '',
  url: '',
  item_selector: '',
  title_selector: '',
  date_attr: '',
  drop_past: true,
  max_items: DIGEST_DEFAULT_MAX_ITEMS,
  page_url_template: '',
  fetch_detail_pages: false,
  detail_link_selector: '',
  max_pages: DIGEST_DEFAULT_MAX_PAGES,
};

const addRowButtonStyle: CSSProperties = {
  textTransform: 'none',
  height: 'auto',
  minHeight: '36px',
  gap: '0.35rem',
  borderColor: 'var(--primary-color)',
  color: 'var(--primary-color)',
  backgroundColor: '#FFFFFF',
  fontSize: '0.8125rem',
  letterSpacing: '0.01em',
};

// Registers webscrap_digests as an array of digest configs - each one turns
// a listing page into a clean, ordered, date-filtered summary document
// (e.g. an events page turned into a "what's coming up" list), since normal
// scraping can't sort by date or drop past items. Matches the backend's
// WebscrapConfig.digests (see libs/webscrap/config.py DigestRule). Laid out
// the same flat way as WebscrapLinksField (plain label header, numbered
// dividers between entries) rather than a collapsible bordered accordion,
// so it reads consistently with the other repeated-entry fields on this
// form instead of standing out as its own boxed widget.
const DigestsConfigFields = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'webscrap_digests',
  });

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <Text type="label">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            {t('Digests')}
            <ToolTip
              title={t(
                'Turn this listing page into a clean, ordered, filtered list, sorted by date with past items dropped. Useful for an events or catalogue page you want summarized as "what\'s coming up".'
              )}
            />
          </Box>
        </Text>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          size="small"
          padding="0.35rem 0.85rem"
          style={addRowButtonStyle}
          onClick={() => append(EMPTY_DIGEST)}
        >
          <Add sx={{ fontSize: '1rem' }} />
          {t('Add digest')}
        </Button>
      </Box>

      {fields.map((field, index) => (
        <Box key={field.id} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Divider
            sx={{
              marginTop: index === 0 ? '0.75rem' : '1rem',
              marginBottom: '0.75rem',
              fontSize: '0.75rem',
              color: '#94A3B8',
              '&::before, &::after': {
                borderColor: 'rgba(15, 23, 42, 0.1)',
              },
            }}
          >
            {index + 1}
          </Divider>
          <Box
            sx={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}
          >
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <DigestEntryFields index={index} />
            </Box>
            <IconButton
              aria-label={t('Remove digest')}
              onClick={() => remove(index)}
              size="small"
              type="button"
              sx={{ color: '#94A3B8', mt: '0.25rem' }}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default DigestsConfigFields;
